import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaWhatsapp, FaPhone } from "react-icons/fa";
import { FiMessageCircle, FiX } from "react-icons/fi";

export default function Chatbot() {
  const [open, setOpen] = useState(false);

  const phoneNumber = "919041429065";
  const callNumber = "+919041429065";

  // ─── Styles ───────────────────────────────────────────────
  const colors = {
    primary: "#6366f1",
    primaryDark: "#4f46e5",
    accent: "#a855f7",
    green: "#22c55e",
    greenDark: "#16a34a",
    bg: "#0d0d14",
    bgLight: "rgba(15, 15, 25, 0.9)",
    border: "rgba(255, 255, 255, 0.08)",
    text: "#f0f0f5",
    textMuted: "#8888a0",
  };

  const styles = {
    container: {
      position: "fixed",
      right: "24px",
      bottom: "100px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "12px",
      zIndex: 9998,
    },

    // Main toggle button
    mainButton: {
      width: "60px",
      height: "60px",
      borderRadius: "18px",
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
      color: "white",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
      border: "none",
      boxShadow: `0 8px 32px rgba(99, 102, 241, 0.4)`,
      transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
      position: "relative",
      overflow: "hidden",
    },

    mainButtonInner: {
      position: "relative",
      zIndex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "1.4rem",
    },

    // Pulse ring
    pulseRing: {
      position: "absolute",
      inset: "-4px",
      borderRadius: "22px",
      border: `2px solid ${colors.primary}`,
      opacity: open ? 0 : 0.5,
      animation: "chatbot-pulse 2s infinite",
      pointerEvents: "none",
    },

    // Action button base
    actionButton: {
      width: "50px",
      height: "50px",
      borderRadius: "16px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      fontSize: "1.2rem",
      textDecoration: "none",
      cursor: "pointer",
      border: `1px solid ${colors.border}`,
      transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
      backdropFilter: "blur(20px)",
    },

    // WhatsApp button
    whatsappButton: {
      background: "rgba(34, 197, 94, 0.15)",
      color: colors.green,
      borderColor: "rgba(34, 197, 94, 0.3)",
    },

    // Phone button
    phoneButton: {
      background: "rgba(99, 102, 241, 0.15)",
      color: colors.primary,
      borderColor: "rgba(99, 102, 241, 0.3)",
    },

    // Tooltip
    tooltip: {
      position: "absolute",
      right: "60px",
      top: "50%",
      transform: "translateY(-50%)",
      background: colors.bgLight,
      border: `1px solid ${colors.border}`,
      borderRadius: "10px",
      padding: "6px 12px",
      fontSize: "0.75rem",
      fontWeight: "600",
      color: colors.text,
      whiteSpace: "nowrap",
      backdropFilter: "blur(20px)",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
      pointerEvents: "none",
    },

    // Label
    label: {
      fontSize: "0.65rem",
      fontWeight: "700",
      color: colors.textMuted,
      textTransform: "uppercase",
      letterSpacing: "1px",
      marginTop: "-4px",
    },
  };

  // ─── Hover Handlers ───────────────────────────────────────
  const handleMainHover = (e, enter) => {
    if (enter) {
      e.currentTarget.style.transform = "scale(1.1)";
      e.currentTarget.style.boxShadow = "0 12px 40px rgba(99, 102, 241, 0.6)";
    } else {
      e.currentTarget.style.transform = "scale(1)";
      e.currentTarget.style.boxShadow = "0 8px 32px rgba(99, 102, 241, 0.4)";
    }
  };

  const handleActionHover = (e, enter, type) => {
    if (enter) {
      e.currentTarget.style.transform = "scale(1.1) translateX(-4px)";
      if (type === "whatsapp") {
        e.currentTarget.style.background = "rgba(34, 197, 94, 0.25)";
        e.currentTarget.style.borderColor = "rgba(34, 197, 94, 0.5)";
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(34, 197, 94, 0.2)";
      } else {
        e.currentTarget.style.background = "rgba(99, 102, 241, 0.25)";
        e.currentTarget.style.borderColor = "rgba(99, 102, 241, 0.5)";
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(99, 102, 241, 0.2)";
      }
    } else {
      e.currentTarget.style.transform = "scale(1)";
      e.currentTarget.style.boxShadow = "none";
      if (type === "whatsapp") {
        e.currentTarget.style.background = "rgba(34, 197, 94, 0.15)";
        e.currentTarget.style.borderColor = "rgba(34, 197, 94, 0.3)";
      } else {
        e.currentTarget.style.background = "rgba(99, 102, 241, 0.15)";
        e.currentTarget.style.borderColor = "rgba(99, 102, 241, 0.3)";
      }
    }
  };

  return (
    <>
      {/* Inject keyframe animation */}
      <style>{`
        @keyframes chatbot-pulse {
          0% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.15); opacity: 0; }
          100% { transform: scale(1); opacity: 0.5; }
        }
      `}</style>

      <div style={styles.container}>
        {/* Action Buttons */}
        <AnimatePresence>
          {open && (
            <>
              {/* WhatsApp */}
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                transition={{ delay: 0.1, type: "spring", damping: 20 }}
                style={{ position: "relative" }}
              >
                <a
                  href={`https://api.whatsapp.com/send?phone=${phoneNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ ...styles.actionButton, ...styles.whatsappButton }}
                  onMouseEnter={(e) => handleActionHover(e, true, "whatsapp")}
                  onMouseLeave={(e) => handleActionHover(e, false, "whatsapp")}
                >
                  <FaWhatsapp />
                </a>
                <motion.div
                  style={styles.tooltip}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  WhatsApp
                </motion.div>
              </motion.div>

              {/* Phone */}
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                transition={{ delay: 0.05, type: "spring", damping: 20 }}
                style={{ position: "relative" }}
              >
                <a
                  href={`tel:${callNumber}`}
                  style={{ ...styles.actionButton, ...styles.phoneButton }}
                  onMouseEnter={(e) => handleActionHover(e, true, "phone")}
                  onMouseLeave={(e) => handleActionHover(e, false, "phone")}
                >
                  <FaPhone />
                </a>
                <motion.div
                  style={styles.tooltip}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 }}
                >
                  Call Me
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Main Toggle Button */}
        <motion.div
          style={styles.mainButton}
          onClick={() => setOpen(!open)}
          onMouseEnter={(e) => handleMainHover(e, true)}
          onMouseLeave={(e) => handleMainHover(e, false)}
          whileTap={{ scale: 0.95 }}
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ type: "spring", damping: 15 }}
        >
          {!open && <div style={styles.pulseRing} />}
          <div style={styles.mainButtonInner}>
            {open ? <FiX /> : <FiMessageCircle />}
          </div>
        </motion.div>

        <span style={styles.label}>
          {open ? "Close" : "Chat"}
        </span>
      </div>
    </>
  );
}
